import { Colors, TextArea } from '@blueprintjs/core';
import BOSDateInput from 'Components/Generic/BOSDateInput';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import UserSelect from 'Components/Generic/BOSFilterSelect/UserSelect';
import VendorSelect from 'Components/Generic/BOSFilterSelect/VendorSelect';
import BOSSelect from 'Components/Generic/BOSSelect';
import ServiceContractSelect from 'Components/Generic/BOSSelect/ServiceContractSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import { useShippingContext } from 'Data/context/Shipping';
import { user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import { GetTransactionDataBus, TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getTagValue, setTagValue } from 'Util/tagUtil';
import { TRANSACTION_CATEGORY, TRANSACTION_TYPE } from '../Enum';

let DM = new DataManager();
const selectWidth = 200;
let OrgAddressData = GenericTableData.getTableDataBus(
  'ORG_ADD',
  'organization_addresses',
);
let UserAddressData = GenericTableData.getTableDataBus(
  'USER_ADD',
  'user_addresses',
);

export const useEditableTransactionInformation = ({
  TransactionDataBus = GetTransactionDataBus(),
  transactionObject = {},
  hideDeliveryAddress = false,
  setEditState = () => {},
  isNew = false,
}) => {
  const {
    currentObject: transaction,
    hasBeenUpdated,
    reset: resetTransaction,
    updateField,
    originalObject: originalTransaction,
    doSave,
  } = useUpdatableObject(TransactionDataBus.updateTransaction, (newTrans) =>
    TransactionData.createNewTransaction(
      newTrans,
      deliveryAddressIdRef.current,
    ),
  );

  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalTransaction,
    undoFunction: TransactionDataBus.updateTransaction,
    onUndo: (originalTransaction) => fetchTransaction(originalTransaction.id),
    objectType: 'Transaction',
  });
  const [userAddressIdList, setUserAddressIdList] = useState([]);
  const [orgAddressIdList, setOrgAddressIdList] = useState([]);
  const [, setDeliveryAddressId, deliveryAddressIdRef] = useStateWithRef();

  const shippingContext = useShippingContext();
  const navigate = useNavigate();

  let shippingItem = shippingContext?.shippingItem;

  useEffect(() => {
    DM.add(
      TransactionDataBus.registerListener('RECORD_LOADED', onTransactionLoaded),
      TransactionDataBus,
    );
    DM.add(
      TransactionDataBus.registerListener(
        'RECORD_UPDATED',
        onTransactionUpdated,
      ),
      TransactionDataBus,
    );
    DM.add(
      OrgAddressData.registerListener('LIST_LOADED', onOrgAddressesLoaded),
      OrgAddressData,
    );
    DM.add(
      UserAddressData.registerListener('LIST_LOADED', onUserAddressesLoaded),
      UserAddressData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setEditState(undefined);
  }, []);

  useEffect(() => {
    resetTransactionTo(transactionObject);
  }, [transactionObject]);

  const onTransactionLoaded = (payload) => {
    if (payload) {
      resetTransactionTo(payload);
    }
  };

  const resetTransactionTo = (updatedTransObject) => {
    resetTransaction(updatedTransObject);
    resetUndo(() => fetchTransaction(updatedTransObject.id));
  };

  const fetchTransaction = (id) => {
    TransactionDataBus.getTransaction(id);
  };

  useEffect(() => {
    shippingItem?.delivery_address_id &&
      setDeliveryAddressId(shippingItem.delivery_address_id);
  }, [shippingItem?.delivery_address_id]);

  const onTransactionUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
    }
  };

  useEffect(() => {
    if (transaction.created_by) {
      UserAddressData.getRecordList({
        where: { '*user_id': transaction.created_by },
      });
    }
  }, [transaction.created_by]);

  const onUserAddressesLoaded = (payload) => {
    if (payload) {
      setUserAddressIdList(payload.records.map((record) => record.address_id));
    }
  };

  useEffect(() => {
    if (transaction.parent_organization_id) {
      OrgAddressData.getRecordList({
        where: { '*organization_id': transaction.parent_organization_id },
      });
    }
  }, [transaction.parent_organization_id]);

  const onOrgAddressesLoaded = (payload) => {
    if (payload) {
      setOrgAddressIdList(payload.records.map((record) => record.address_id));
    }
  };

  useEffect(() => {
    let addressIdList = [
      ...(shippingItem?.delivery_address_id
        ? [shippingItem.delivery_address_id]
        : []),
      ...userAddressIdList.filter(
        (addId) => addId !== shippingItem?.delivery_address_id,
      ),
      ...orgAddressIdList.filter(
        (addId) => addId !== shippingItem?.delivery_address_id,
      ),
    ];
    if (
      shippingContext?.updateShipping &&
      shippingItem?.delivery_address_id &&
      !addressIdList.includes(shippingItem.delivery_address_id)
    ) {
      shippingContext.updateShipping('delivery_address_id', null);
    }
    shippingContext?.setDeliveryAddressOptions &&
      shippingContext.setDeliveryAddressOptions(addressIdList);
  }, [userAddressIdList, orgAddressIdList]);

  let shippingDeliveryUpdated =
    shippingContext?.updatedFields &&
    Object.hasOwn(shippingContext.updatedFields, 'delivery_address_id');

  const saveClicked = () => {
    if (shippingContext?.saveShipping) {
      shippingContext.saveShipping().then(() => {
        hasBeenUpdated && doSave();
        !hasBeenUpdated && setEditState(false);
      });
    } else {
      console.log('save clicked function');
      hasBeenUpdated && doSave();
      !hasBeenUpdated && setEditState(false);
    }
  };

  let data = [
    {
      label: 'Category',
      value: (
        <BOSSelect
          defaultSelectedId={transaction?.category}
          onItemSelect={(category) => {
            updateField('category', category?.key || null);
          }}
          listData={Object.entries(TRANSACTION_CATEGORY).map(([key, value]) => {
            return {
              key,
              value,
            };
          })}
          idField={'key'}
          labelField={'value'}
          isRequired
        />
      ),
    },

    {
      label: 'Core Facility',
      value: (
        <div style={{ minWidth: selectWidth }}>
          <OrganizationSelect
            key={transaction.parent_organization_id}
            defaultSelectedId={transaction.parent_organization_id}
            onSelectOrganization={(org) =>
              updateField('parent_organization_id', org?.id || null)
            }
            isRequired
          />
        </div>
      ),
    },

    {
      label: 'Vendor',
      value: (
        <VendorSelect
          key={transaction.vendor_id}
          defaultSelectedId={transaction.vendor_id}
          onSelectVendor={(vendor) =>
            updateField('vendor_id', vendor?.id || null)
          }
          isRequired
        />
      ),
    },

    {
      label: 'Transaction Type',
      value: (
        <BOSSelect
          defaultSelectedId={transaction?.transaction_type}
          onItemSelect={(type) => {
            updateField('transaction_type', type?.key || null);
          }}
          listData={Object.entries(TRANSACTION_TYPE).map(([key, value]) => {
            return {
              key,
              value,
            };
          })}
          idField={'key'}
          labelField={'value'}
          isRequired
        />
      ),
    },

    {
      label: 'Service Contract Transaction',
      value: (
        <div style={{ display: 'flex' }}>
          <input
            type='checkbox'
            checked={getTagValue(transaction, 'is_service_contract') === 'true'}
            onChange={(event) => {
              updateField(
                'tags',
                setTagValue(
                  transaction,
                  'is_service_contract',
                  event.target.checked,
                ),
              );
            }}
          />
        </div>
      ),
    },

    ...(getTagValue(transaction, 'is_service_contract')
      ? [
          {
            label: 'Service Contract',
            value: (
              <ServiceContractSelect
                orgId={transaction?.parent_organization_id}
                defaultSelectedId={
                  transaction?.service_contracts?.length > 0
                    ? transaction.service_contracts[0].id
                    : null
                }
                onSelectServiceContract={(sc) =>
                  updateField('service_contracts', sc?.id ? [sc] : [])
                }
              />
            ),
          },
        ]
      : []),

    {
      label: 'Transaction Date',
      value: (
        <div style={{ minWidth: selectWidth }}>
          <BOSDateInput
            value={transaction.date_purchased}
            onChange={(date) => updateField('date_purchased', date)}
          />
        </div>
      ),
    },

    {
      label: 'Purchaser',
      value: (
        <UserSelect
          key={`rq-${transaction.purchased_by}`}
          defaultSelectedId={transaction.purchased_by}
          onSelectUser={(user) => updateField('purchased_by', user?.id || null)}
          userRoles={[user_roles.BUYER.key, user_roles.TRANSACTOR.key]}
          includedId={originalTransaction.purchased_by}
        />
      ),
    },

    ...(!hideDeliveryAddress
      ? [
          {
            label: 'Delivery Address',
            value: shippingContext?.deliveryAddressSelect,
          },
        ]
      : []),

    {
      label: 'Reference Number',
      value: transaction.reference_number,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('reference_number', newValue);
      },
    },

    {
      label: 'Confirmation Number',
      value: transaction.confirmation_number,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('confirmation_number', newValue);
      },
    },
    {
      label: 'Description',
      value: (
        <TextArea
          fill
          autoResize
          value={transaction.notes}
          onChange={(e) => {
            updateField('notes', e.target.value);
          }}
        />
      ),
    },

    {
      label: <span style={{ color: Colors.RED2 }}>Radioactive</span>,
      value: (
        <div style={{ display: 'flex' }}>
          <input
            type='checkbox'
            checked={getTagValue(transaction, 'is_radioactive')}
            onChange={(event) => {
              updateField(
                'tags',
                setTagValue(
                  transaction,
                  'is_radioactive',
                  event.target.checked,
                ),
              );
            }}
          />
        </div>
      ),
    },
  ];

  let undoClickHandler = () => {
    fetchTransaction(originalTransaction.id);
  };

  const undoButton = !isNew
    ? (hasBeenUpdated || shippingDeliveryUpdated) && (
        <UndoButton clickHandler={undoClickHandler} />
      )
    : !transaction.id && (
        <CancelButton fill={false} onCancel={() => navigate(-1)} />
      );

  const requiredFieldsPresent =
    transaction?.parent_organization_id &&
    transaction?.vendor_id &&
    transaction?.category &&
    transaction?.transaction_type;

  const saveButton = (hasBeenUpdated || shippingDeliveryUpdated) && (
    <SaveButton clickHandler={saveClicked} disabled={!requiredFieldsPresent} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  const editableTable = <DetailTable data={data} editable />;

  return { editableTable, footer, transaction };
};
